import React, { useState, useEffect } from "react";
import { useAuth, UserButton } from "@clerk/clerk-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { toast } from "../hooks/use-toast";
import { Loader2, Settings, Save, Home } from "lucide-react";
import { Link } from "react-router-dom";

const DashboardSettings = () => {
  const { getToken, user, isLoaded } = useAuth();
  const [settings, setSettings] = useState({
    goldSpot: "",
    platinumSpot: "",
    silverSpot: "",
    goldsmithWage: "",
    retailMultiplier: "",
    laserPreference: "Torch",
    storeName: "",
    userName: "",
    quoteValidDays: ""
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const workerBaseUrl = process.env.REACT_APP_WORKER_BASE_URL;

  useEffect(() => {
    fetchSettings();
  },[]); 

  const fetchSettings = async () => {
    try {
      // Wait for user to be loaded
      // if (!user) {
      //   console.log('User not authenticated yet');
      //   setIsLoading(false);
      //   return;
      // }

      // console.log('Fetching settings from:', `${workerBaseUrl}/settings`);
      
      const token = await getToken();
      
      const response = await fetch(`${workerBaseUrl}/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        // console.log('Settings loaded:', data);
        
        setSettings({
          goldSpot: data.goldSpot?.toString() || "3300",
          platinumSpot: data.platinumSpot?.toString() || "1398",
          silverSpot: data.silverSpot?.toString() || "38",
          goldsmithWage: data.goldsmithWage?.toString() || "75",
          retailMultiplier: data.retailMultiplier?.toString() || "2.5",
          laserPreference: data.laserPreference || "Torch",
          storeName: data.storeName || "",
          userName: data.userName || "",
          quoteValidDays: data.quoteValidDays?.toString() || "14"
        });
        
        // Clear any existing errors when settings are loaded
        setErrors({});
      } else {
        console.error('Failed to fetch settings:', response.status);
        // Use defaults if API fails
        setSettings({
          goldSpot: "3300",
          platinumSpot: "1398",
          silverSpot: "38",
          goldsmithWage: "75",
          retailMultiplier: "2.5",
          laserPreference: "Torch",
          storeName: "",
          userName: "",
          quoteValidDays: "14"
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      
      // Fallback to defaults on error
        setSettings({
          goldSpot: "3300",
          platinumSpot: "1398",
          silverSpot: "38",
          goldsmithWage: "75",
          retailMultiplier: "2.5",
          laserPreference: "Torch",
          storeName: "",
          userName: "",
          quoteValidDays: "14"
        });
      
      toast({
        title: "Error",
        description: "Failed to load settings. Using defaults.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };



  const validateField = (field, value) => {
    const error = {};
    
    // Convert value to string and trim for validation
    const stringValue = value ? String(value).trim() : '';
    
    switch (field) {
      case 'goldSpot':
      case 'platinumSpot':
      case 'silverSpot':
        if (!stringValue || stringValue === '') {
          error[field] = 'This field is required';
        } else {
          const num = parseFloat(stringValue);
          if (isNaN(num) || num <= 0) {
            error[field] = 'Must be a positive number';
          }
        }
        break;
      
      case 'goldsmithWage':
        if (!stringValue || stringValue === '') {
          error[field] = 'This field is required';
        } else {
          const num = parseFloat(stringValue);
          if (isNaN(num) || num <= 0) {
            error[field] = 'Must be a positive number';
          }
        }
        break;
      
      case 'retailMultiplier':
        if (!stringValue || stringValue === '') {
          error[field] = 'This field is required';
        } else {
          const num = parseFloat(stringValue);
          if (isNaN(num) || num <= 0) {
            error[field] = 'Must be a positive number';
          } else if (num < 1) {
            error[field] = 'Must be at least 1.0';
          }
        }
        break;
      
      case 'quoteValidDays':
        if (!stringValue || stringValue === '') {
          error[field] = 'This field is required';
        } else {
          const num = parseInt(stringValue, 10);
          if (isNaN(num) || num < 1) {
            error[field] = 'Must be a positive integer (at least 1)';
          }
        }
        break;
      
      case 'laserPreference':
        const validOptions = ['Torch', 'Laser', 'Prompt Me'];
        if (!value || !validOptions.includes(value)) {
          error[field] = 'Please select a valid option';
        }
        break;
      
      default:
        break;
    }
    
    return error;
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleBlur = (field) => {
    const value = settings[field];
    const fieldError = validateField(field, value);
    setErrors(prev => ({
      ...prev,
      ...fieldError
    }));
  };

  const validateAllFields = () => {
    const newErrors = {};
    
    // Validate all fields except user information
    const fieldsToValidate = [
      'goldSpot',
      'platinumSpot',
      'silverSpot',
      'goldsmithWage',
      'retailMultiplier',
      'quoteValidDays',
      'laserPreference'
    ];
    
    fieldsToValidate.forEach(field => {
      const fieldError = validateField(field, settings[field]);
      Object.assign(newErrors, fieldError);
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submitting
    if (!validateAllFields()) {
      toast({
        title: "Validation Error",
        description: "Please fix all errors before saving.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSaving(true);

    try {
      const token = await getToken();
      // console.log('Saving settings to:', `${workerBaseUrl}/settings`);
      
      const response = await fetch(`${workerBaseUrl}/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
          body: JSON.stringify({
          goldSpot: parseFloat(settings.goldSpot) || 0,
          platinumSpot: parseFloat(settings.platinumSpot) || 0,
          silverSpot: parseFloat(settings.silverSpot) || 0,
          goldsmithWage: parseFloat(settings.goldsmithWage) || 0,
          retailMultiplier: parseFloat(settings.retailMultiplier) || 0,
          laserPreference: settings.laserPreference,
          storeName: settings.storeName,
          userName: settings.userName,
          quoteValidDays: parseInt(settings.quoteValidDays) || 14
        })
      });

      if (response.ok) {
        const result = await response.json();
        // console.log('Settings saved:', result);
        
        toast({
          title: "Success",
          description: "Settings saved successfully!",
          variant: "default"
        });
        
        // Refresh settings from server
        await fetchSettings();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to save: ${response.status}`);
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save settings. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };



  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-slate-600">Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
              <Home className="h-5 w-5" />
              <span>Back to Quotes</span>
            </Link>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2">
              <Settings className="h-6 w-6" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">StudioIQ Settings</h1>
                <p className="text-sm text-slate-600">{user?.emailAddresses[0]?.emailAddress}</p>
              </div>
            </div>
          </div>
          <UserButton afterSignOutUrl="/" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Metal Prices */}
          <Card>
            <CardHeader>
              <CardTitle>Metal Spot Prices (per oz)</CardTitle>
              <CardDescription>
                Current market prices for precious metals used in jewelry pricing calculations
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="goldSpot">Gold ($)</Label>
                <Input
                  id="goldSpot"
                  type="number"
                  step="0.01"
                  placeholder="3300.00"
                  value={settings.goldSpot}
                  onChange={(e) => handleInputChange('goldSpot', e.target.value)}
                  onBlur={() => handleBlur('goldSpot')}
                  className={errors.goldSpot ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {errors.goldSpot && (
                  <p className="text-sm text-red-600">{errors.goldSpot}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="platinumSpot">Platinum ($)</Label>
                <Input
                  id="platinumSpot"
                  type="number"
                  step="0.01"
                  placeholder="1398.00"
                  value={settings.platinumSpot}
                  onChange={(e) => handleInputChange('platinumSpot', e.target.value)}
                  onBlur={() => handleBlur('platinumSpot')}
                  className={errors.platinumSpot ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {errors.platinumSpot && (
                  <p className="text-sm text-red-600">{errors.platinumSpot}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="silverSpot">Silver ($)</Label>
                <Input
                  id="silverSpot"
                  type="number"
                  step="0.01"
                  placeholder="38.00"
                  value={settings.silverSpot}
                  onChange={(e) => handleInputChange('silverSpot', e.target.value)}
                  onBlur={() => handleBlur('silverSpot')}
                  className={errors.silverSpot ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {errors.silverSpot && (
                  <p className="text-sm text-red-600">{errors.silverSpot}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quote Validity */}
          <Card>
            <CardHeader>
              <CardTitle>Quote Validity</CardTitle>
              <CardDescription>
                Number of days the quote remains valid for customers
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quoteValidDays">Quote Valid For (days)</Label>
                <Input
                  id="quoteValidDays"
                  type="number"
                  step="1"
                  min="1"
                  placeholder="14"
                  value={settings.quoteValidDays}
                  onChange={(e) => handleInputChange('quoteValidDays', e.target.value)}
                  onBlur={() => handleBlur('quoteValidDays')}
                  className={errors.quoteValidDays ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {errors.quoteValidDays && (
                  <p className="text-sm text-red-600">{errors.quoteValidDays}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Labor & Markup */}
          <Card>
            <CardHeader>
              <CardTitle>Labor & Markup Settings</CardTitle>
              <CardDescription>
                Configure labor costs and retail pricing multipliers
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="goldsmithWage">Labor Wage ($/hour)</Label>
                <Input
                  id="goldsmithWage"
                  type="number"
                  step="0.01"
                  placeholder="75.00"
                  value={settings.goldsmithWage}
                  onChange={(e) => handleInputChange('goldsmithWage', e.target.value)}
                  onBlur={() => handleBlur('goldsmithWage')}
                  className={errors.goldsmithWage ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {errors.goldsmithWage && (
                  <p className="text-sm text-red-600">{errors.goldsmithWage}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="retailMultiplier">Retail Mark-up (multiplier)</Label>
                <Input
                  id="retailMultiplier"
                  type="number"
                  step="0.01"
                  placeholder="2.5"
                  value={settings.retailMultiplier}
                  onChange={(e) => handleInputChange('retailMultiplier', e.target.value)}
                  onBlur={() => handleBlur('retailMultiplier')}
                  className={errors.retailMultiplier ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {errors.retailMultiplier && (
                  <p className="text-sm text-red-600">{errors.retailMultiplier}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Laser Preference */}
          <Card>
            <CardHeader>
              <CardTitle>Laser Preference</CardTitle>
              <CardDescription>
                Choose your preferred welding/repair method
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={settings.laserPreference}
                onValueChange={(value) => {
                  handleInputChange('laserPreference', value);
                  // Clear error when selection changes
                  if (errors.laserPreference) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.laserPreference;
                      return newErrors;
                    });
                  }
                }}
                className="flex flex-col space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Torch" id="torch" />
                  <Label htmlFor="torch">Torch</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Laser" id="laser" />
                  <Label htmlFor="laser">Laser (1.5x Mark-up)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Prompt Me" id="prompt" />
                  <Label htmlFor="prompt">Prompt Me</Label>
                </div>
              </RadioGroup>
              {errors.laserPreference && (
                <p className="text-sm text-red-600 mt-2">{errors.laserPreference}</p>
              )}
            </CardContent>
          </Card>

          {/* User Info */}
          <Card>
            <CardHeader>
              <CardTitle>User Information</CardTitle>
              <CardDescription>
                Optional information for personalizing quotes and invoices
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="storeName">Store Name</Label>
                <Input
                  id="storeName"
                  placeholder="Your Jewelry Store"
                  value={settings.storeName}
                  onChange={(e) => handleInputChange('storeName', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="userName">User Name</Label>
                <Input
                  id="userName"
                  placeholder="John Doe"
                  value={settings.userName}
                  onChange={(e) => handleInputChange('userName', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={isSaving}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DashboardSettings;